import { createContext, useContext, useEffect, useState } from 'react'
import { portalApi } from '../services/api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('portal_user')
      return saved ? JSON.parse(saved) : null
    } catch { return null }
  })
  const [loading, setLoading] = useState(false)

  // Re-validate the token on app load. If portal_token + portal_user are out
  // of sync (stale token from a different account, expired token, etc.), the
  // /auth/me call either 401s (we evict the session) or returns a different
  // user (we overwrite the cache). Prevents the cross-account data leak that
  // happened when a stale info@chantsit.com token was paired with a cached
  // todd.chant@integolf.com user object.
  useEffect(() => {
    const token = localStorage.getItem('portal_token')
    if (!token) return // not logged in; nothing to validate
    portalApi.get('/auth/me')
      .then(({ data }) => {
        const liveUser = data?.user
        if (!liveUser) return
        try {
          const cachedRaw = localStorage.getItem('portal_user')
          const cached = cachedRaw ? JSON.parse(cachedRaw) : null
          // If the token's user doesn't match the cached user (different ID
          // or different email), the cache is stale — overwrite.
          if (!cached || cached.ID_NO !== liveUser.ID_NO || cached.email !== liveUser.email) {
            localStorage.setItem('portal_user', JSON.stringify(liveUser))
            setUser(liveUser)
            console.warn('[Auth] cached user replaced with live /auth/me result (stale localStorage)')
          }
        } catch { /* ignore parse errors */ }
      })
      .catch(err => {
        if (err.response?.status === 401) {
          // Token invalid/expired — evict everything and let the user log in fresh.
          localStorage.removeItem('portal_token')
          localStorage.removeItem('portal_user')
          setUser(null)
          console.warn('[Auth] stale token evicted (401 on /auth/me)')
        }
        // For other errors, leave state alone so transient API issues don't log users out.
      })
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function login(userData, token) {
    localStorage.setItem('portal_token', token)
    localStorage.setItem('portal_user', JSON.stringify(userData))
    setUser(userData)
  }

  function logout() {
    localStorage.removeItem('portal_token')
    localStorage.removeItem('portal_user')
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
